import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { integrationsStatusSummary } from "@/services/integrations";
import type { IntegrationsStatusSummaryResponse } from "@/types";

/**
 * ONE read of every integration's connection state for the selected app —
 * `GET /api/v1/integrations/{app_id}/status-summary` — for the two app-shell
 * cards (`ConnectStoreCard`, `AddRicherSignalsCard`) that render on every
 * authenticated route. Before this hook the shell fired seven per-integration
 * `…/{app_id}/status` probes on every fresh route load (ENG-APP-UX-TUNEUP
 * floor row 11: 13 shell calls on `/`, target ≤ 8); the settings pages keep
 * their own per-integration hooks, this one only feeds the shell.
 *
 * Freshness: a 60 s `staleTime` (the ticket's floor for status probes) plus a
 * QueryCache subscription — whenever a per-integration status query for this
 * app is invalidated (every connect / disconnect / reuse-paired mutation in
 * the seven hooks does exactly that), the summary is invalidated too, so the
 * shell cards never lag a settings-page action by the stale window.
 */

export const INTEGRATIONS_STATUS_SUMMARY_KEY_ROOT =
  "integrations_status_summary";

/** Row 11 of the ENG-APP-UX-TUNEUP floor table: status probes `staleTime` ≥ 60 s. */
export const INTEGRATIONS_STATUS_SUMMARY_STALE_MS = 60_000;

export function integrationsStatusSummaryKey(appId: string | null) {
  return [INTEGRATIONS_STATUS_SUMMARY_KEY_ROOT, "status", appId] as const;
}

export function useIntegrationsStatusSummary(appId: string | null) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!appId) return undefined;
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "invalidate")
        return;
      const key = event.query.queryKey;
      if (key[0] === INTEGRATIONS_STATUS_SUMMARY_KEY_ROOT) return;
      if (key[1] !== "status" || key[2] !== appId) return;
      void queryClient.invalidateQueries({
        queryKey: integrationsStatusSummaryKey(appId),
      });
    });
  }, [appId, queryClient]);

  return useQuery<IntegrationsStatusSummaryResponse, Error>({
    queryKey: integrationsStatusSummaryKey(appId),
    queryFn: () => {
      if (!appId) throw new Error("appId required");
      return integrationsStatusSummary(appId);
    },
    enabled: Boolean(appId),
    staleTime: INTEGRATIONS_STATUS_SUMMARY_STALE_MS,
  });
}
